<template>
  <div v-if="show" class="global-search" @click="closeIfClickedOutside">
    <div class="search-modal" @click.stop>
      <div class="search-header">
        <input
          ref="searchInput"
          v-model="searchQuery"
          type="search"
          :placeholder="`Buscar ${customerTreatmentPlural.toLowerCase()}${usesTasks ? ' ou tarefas' : ''}…`"
          class="search-input"
          autocomplete="off"
          @keydown.down.prevent="move(1)"
          @keydown.up.prevent="move(-1)"
          @keydown.enter.prevent="enter"
        />
        <button class="btn-close" title="Fechar" @click="close">
          <img src="~@/assets/img/ico/ico_close.svg" alt="" />
        </button>
      </div>

      <div class="search-results">
        <!-- Before typing: the patients likely wanted now -->
        <ComponentPatientShortcuts
          v-if="query.length < 2"
          :title="shortcutsTitle"
          where="search"
          compact
          :active-index="active"
          :preload="shortcuts"
          @loaded="shortcuts = $event"
          @hover="active = $event"
          @picked="close"
        />
        <template v-else-if="loading && !customers">
          <div class="search-loading"><p>Buscando…</p></div>
        </template>
        <template v-else-if="customerResults.length || taskResults.length">
          <div v-if="customerResults.length" class="result-group">
            <div class="result-group-header">
              <img src="~@/assets/img/ico/ico_clients.svg" alt="" />
              <span>{{ customerTreatmentPlural }}</span>
            </div>
            <div
              v-for="(customer, i) in customerResults"
              :key="'customer-' + customer.id"
              class="result-item"
              :class="{ active: i === active }"
              @mouseenter="active = i"
            >
              <div class="result-content" @click="goToCustomer(customer.id)">
                <div class="result-title">{{ customer.name }}</div>
              </div>
              <button v-if="canWriteNotes" type="button" class="result-note" @click="goToCustomer(customer.id, true)">Anotar</button>
            </div>
          </div>

          <div v-if="taskResults.length" class="result-group">
            <div class="result-group-header">
              <img src="~@/assets/img/ico/ico_models.svg" alt="" />
              <span>Tarefas</span>
            </div>
            <div
              v-for="task in taskResults"
              :key="'task-' + task.id"
              class="result-item"
              @click="goToTaskBoard(task.task_list_id)"
            >
              <div class="result-content">
                <div class="result-title">{{ task.title }}</div>
                <div v-if="task.customer" class="result-meta">{{ task.customer.name }}</div>
              </div>
              <div class="result-badge" :class="'status-' + task.status">{{ task.status_text }}</div>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="no-results">
            <p>Nenhum resultado para "{{ query }}"</p>
            <p class="no-results-tip">Busca pelo nome, sem precisar de acento.</p>
          </div>
        </template>
      </div>
      <div v-if="!isMobile" class="search-footer">
        <div class="search-tips">
          <span class="tip"><kbd>↑</kbd><kbd>↓</kbd> para navegar</span>
          <span class="tip"><kbd>Enter</kbd> abre a última anotação</span>
          <span class="tip"><kbd>Ctrl</kbd><kbd>K</kbd> abre a busca</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ComponentPatientShortcuts from "@/components/ComponentPatientShortcuts.vue";
import { track } from "@/utils/analytics";

function normalize(s) {
  return String(s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

// Search for patients (and tasks, for accounts that use them). Opens from the header button or
// Ctrl+K; before typing it shows the patients of right now. The patient list is loaded once per
// opening and filtered locally, ignoring accents; Enter opens the record at the last note.
export default {
  name: "GlobalSearch",
  components: { ComponentPatientShortcuts },
  data() {
    return {
      show: false,
      searchQuery: "",
      customers: null,
      tasks: null,
      shortcuts: null,
      loading: false,
      active: 0,
    };
  },
  computed: {
    query() {
      return this.searchQuery.trim();
    },
    company() {
      return this.$store.state.Companies.company || {};
    },
    usesTasks() {
      return !!this.company.uses_tasks;
    },
    isMobile() {
      return ["xs", "sm"].includes(this.$mq.current);
    },
    canWriteNotes() {
      const user = this.$store.getters["App/user"] || {};
      return user.account_type === "standard";
    },
    customerTreatmentPlural() {
      return this.$store.getters["Companies/customerTreatmentPlural"] || "Clientes";
    },
    shortcutsTitle() {
      return `${this.customerTreatmentPlural} de agora`;
    },
    // Names that start with the query first, then any word that starts with it, then anywhere.
    customerResults() {
      const q = normalize(this.query);
      if (q.length < 2 || !this.customers) return [];
      const scored = [];
      this.customers.forEach((c) => {
        const n = normalize(c.name);
        const score = n.startsWith(q) ? 0 : n.split(/\s+/).some((w) => w.startsWith(q)) ? 1 : n.includes(q) ? 2 : -1;
        if (score >= 0) scored.push([score, n, c]);
      });
      return scored
        .sort((a, b) => a[0] - b[0] || a[1].localeCompare(b[1]))
        .slice(0, 8)
        .map((x) => x[2]);
    },
    taskResults() {
      const q = normalize(this.query);
      if (q.length < 2 || !this.tasks) return [];
      return this.tasks.filter((t) => t.title && normalize(t.title).includes(q)).slice(0, 5);
    },
  },
  watch: {
    query() {
      this.active = 0;
    },
  },
  mounted() {
    window.addEventListener("keydown", this.handleKeydown);
    this.$eventbus.on("Search/open", this.open);
  },
  unmounted() {
    window.removeEventListener("keydown", this.handleKeydown);
    this.$eventbus.off("Search/open", this.open);
  },
  methods: {
    open() {
      this.show = true;
      this.searchQuery = "";
      this.active = 0;
      this.shortcuts = null;
      this.customers = null;
      this.tasks = null;
      track("search_open");
      this.load();
      this.$nextTick(() => this.$refs.searchInput && this.$refs.searchInput.focus());
    },
    close() {
      this.show = false;
      this.searchQuery = "";
    },
    closeIfClickedOutside(e) {
      if (e.target.classList.contains("global-search")) this.close();
    },
    async load() {
      this.loading = true;
      try {
        const { data } = await this.$axios.get("/api/v1/customers");
        this.customers = (Array.isArray(data) ? data : []).filter((c) => c && c.name);
        if (this.usesTasks) this.loadTasks();
      } catch (e) {
        this.customers = [];
      } finally {
        this.loading = false;
      }
    },
    async loadTasks() {
      try {
        const { data: lists } = await this.$axios.get("/api/v1/task_lists");
        const all = await Promise.all(
          (lists || []).map((l) =>
            this.$axios.get(`/api/v1/task_lists/${l.id}/tasks`).then(({ data }) => {
              const tasks = Array.isArray(data) ? data : Object.values(data || {}).flat();
              return tasks.map((t) => ({ ...t, task_list_id: l.id }));
            }),
          ),
        );
        this.tasks = all.flat();
      } catch (e) {
        this.tasks = [];
      }
    },
    move(step) {
      const size = this.query.length < 2 ? (this.shortcuts || []).length : this.customerResults.length;
      if (!size) return;
      this.active = (this.active + step + size) % size;
    },
    enter() {
      const list = this.query.length < 2 ? this.shortcuts || [] : this.customerResults;
      const target = list[this.active];
      if (target) this.goToCustomer(target.id);
    },
    goToCustomer(customerId, note = false) {
      track("search_pick", { action: note ? "note" : "record", typed: this.query.length >= 2 });
      this.$router.push({ name: "CustomersDetails", params: { id: customerId }, query: note ? { note: "1" } : { last: "1" } });
      this.close();
    },
    goToTaskBoard(taskListId) {
      this.$router.push({ name: "TaskBoardView", params: { id: taskListId } });
      this.close();
    },
    handleKeydown(e) {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        if (this.show) this.close();
        else this.open();
      }
      if (e.key === "Escape" && this.show) this.close();
    },
  },
};
</script>

<style scoped>
.global-search {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 100000;
  padding: 10vh 1em 0 1em;
}

.search-modal {
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  animation: slideDown 0.2s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.search-header {
  display: flex;
  align-items: center;
  gap: 1em;
  padding: 1.5em;
  border-bottom: 1px solid #e0e0e0;
}

.search-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 1.1em;
  padding: 0.5em 0;
  color: #333;
}

.search-input::placeholder {
  color: #999;
}

.btn-close {
  width: 32px;
  height: 32px;
  border: none;
  background: #f0f0f0;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.btn-close:hover {
  background: #e0e0e0;
}

.btn-close img {
  width: 14px;
  height: 14px;
}

.search-results {
  flex: 1;
  overflow-y: auto;
  padding: 1em;
}

.search-loading,
.no-results {
  text-align: center;
  padding: 3em 1em;
  color: #999;
}

.no-results-tip {
  font-size: 0.9em;
  margin-top: 0.5em;
}

.result-group {
  margin-bottom: 1.5em;
}

.result-group-header {
  display: flex;
  align-items: center;
  gap: 0.5em;
  padding: 0.75em 1em;
  background: #f9f9f9;
  border-radius: 6px;
  margin-bottom: 0.5em;
  font-weight: 600;
  font-size: 0.9em;
  color: #555;
}

.result-group-header img {
  width: 16px;
  height: 16px;
  opacity: 0.6;
}

.result-item {
  display: flex;
  align-items: center;
  gap: 1em;
  padding: 1em;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 0.25em;
}

.result-item.active,
.result-item:hover {
  background: #f5f5f5;
}

.result-note {
  flex-shrink: 0;
  padding: 5px 12px;
  border-radius: 6px;
  border: 1px solid #29b1cc;
  background: #fff;
  color: #127a8f;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
}

.result-content {
  flex: 1;
}

.result-title {
  font-weight: 500;
  color: #333;
  margin-bottom: 0.25em;
}

.result-meta {
  font-size: 0.85em;
  color: #999;
}

.result-badge {
  padding: 0.25em 0.75em;
  border-radius: 4px;
  font-size: 0.8em;
  font-weight: 500;
}

.result-badge.status-completed {
  background: #e8f5e9;
  color: #2e7d32;
}

.result-badge.status-pending {
  background: #fff3e0;
  color: #e65100;
}

.result-badge.status-overdue {
  background: #ffebee;
  color: #c62828;
}

.search-footer {
  padding: 1em 1.5em;
  border-top: 1px solid #e0e0e0;
}

.search-tips {
  display: flex;
  gap: 1.5em;
  font-size: 0.8em;
  color: #999;
}

.tip {
  display: flex;
  align-items: center;
  gap: 0.25em;
}

kbd {
  padding: 0.2em 0.5em;
  background: #f0f0f0;
  border: 1px solid #ddd;
  border-radius: 3px;
  font-family: monospace;
  font-size: 0.9em;
}

@media (max-width: 767px) {
  .search-modal {
    max-height: 90vh;
  }

  .search-tips {
    flex-direction: column;
    gap: 0.5em;
  }
}
</style>
